"use client";

import { Image as ImageType } from "@/types/Image";
import Image from "next/image";
import React, { useCallback, useEffect, useRef, useState } from "react";
import LeftArrow from "../../icons/LeftArrow";
import RightArrow from "../../icons/RightArrow";
import ButtonIcon from "../../utils/ButtonIcon";
import "./index.scss";

const AlbumCarousel = React.forwardRef<
  HTMLUListElement,
  {
    images: ImageType[];
    sizes: string;
    objectFit: "contain" | "cover";
    arrowPosition?: "inside" | "outside";
    onClick?: (imageIndex: number) => void;
  }
>(({ images, sizes, objectFit, arrowPosition = "inside", onClick }, ref) => {
  const fallbackRef = useRef<HTMLUListElement>(null);
  const normalizedRef = ref || fallbackRef;
  const [imageIndex, setImageIndex] = useState(0);

  const handlePreviousClick = useCallback(() => {
    if (normalizedRef) {
      const imageListRef =
        normalizedRef as React.MutableRefObject<HTMLUListElement>;
      const imageListNode = imageListRef.current;
      if (imageListNode) {
        const componentWidth = imageListNode.clientWidth;
        const currentImageIndex = Math.round(
          imageListNode.scrollLeft / componentWidth
        );
        imageListNode.scroll((currentImageIndex - 1) * componentWidth, 0);
      }
    }
  }, [normalizedRef]);

  const handleNextClick = useCallback(() => {
    if (normalizedRef) {
      const imageListRef =
        normalizedRef as React.MutableRefObject<HTMLUListElement>;
      const imageListNode = imageListRef.current;
      if (imageListNode) {
        const componentWidth = imageListNode.clientWidth;
        const currentImageIndex = Math.round(
          imageListNode.scrollLeft / componentWidth
        );
        imageListNode.scroll((currentImageIndex + 1) * componentWidth, 0);
      }
    }
  }, [normalizedRef]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        handlePreviousClick();
      } else if (e.key === "ArrowRight") {
        handleNextClick();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handlePreviousClick, handleNextClick]);

  const imageListRef =
    normalizedRef as React.MutableRefObject<HTMLUListElement>;

  useEffect(() => {
    const scroll = () => {
      const imageListNode = imageListRef.current;

      const componentWidth = imageListNode.clientWidth;
      const scrollLeft = imageListNode.scrollLeft;

      if (scrollLeft === 0) {
        // console.log("Set to 0");
        setImageIndex(0);
      } else {
        // console.log(
        //   componentWidth,
        //   scrollLeft,
        //   Math.floor(scrollLeft / componentWidth)
        // );

        setImageIndex(Math.floor(scrollLeft / componentWidth));
      }
    };

    if (imageListRef.current) {
      imageListRef.current.addEventListener("scroll", scroll);
    }

    return () => {
      if (imageListRef.current) {
        imageListRef.current.removeEventListener("scroll", scroll);
      }
    };
  }, [imageListRef]);

  const imageNodes = images.map((image, imageIndex) => {
    return (
      <li key={`${image.src}-${imageIndex}`}>
        <div className="image-container">
          <Image
            src={image.src}
            alt={image.alt}
            fill
            sizes={sizes}
            onClick={() => (onClick ? onClick(imageIndex) : null)}
            style={{ objectFit: objectFit }}
          />
          {image.caption && image.caption.trim().length > 0 && (
            <div className="caption">
              <div
                className="caption-content"
                dangerouslySetInnerHTML={{ __html: image.caption.trim() }}
              />
            </div>
          )}
        </div>
      </li>
    );
  });

  return (
    <div className={`component album-carousel arrow-position-${arrowPosition}`}>
      <div className="pagination">
        {imageIndex + 1} / {images.length}
      </div>
      <ul ref={normalizedRef}>{imageNodes}</ul>

      <ButtonIcon
        className="previous"
        onClick={(e) => {
          e.preventDefault();
          handlePreviousClick();
        }}
        size={arrowPosition === "inside" ? "medium" : "large"}
        aria-label="Previous Image"
      >
        <LeftArrow />
      </ButtonIcon>
      <ButtonIcon
        className="next"
        onClick={(e) => {
          e.preventDefault();
          handleNextClick();
        }}
        size={arrowPosition === "inside" ? "medium" : "large"}
        aria-label="Next Image"
      >
        <RightArrow />
      </ButtonIcon>
    </div>
  );
});

AlbumCarousel.displayName = "AlbumCarousel";

export default AlbumCarousel;
